'use client';

import {
    FC,
    PropsWithChildren,
    createContext,
    useContext,
    useState,
} from 'react';

interface IMeinModalContext {
    isOpen: boolean;
    openModal: () => void;
    closeModal: () => void;
    toggleModal: () => void;
}

const MeinModalContext = createContext<IMeinModalContext | undefined>(
    undefined,
);

interface MeinModalProviderProps extends PropsWithChildren {
    defaultOpen?: boolean;
}

export const MeinModalProvider: FC<MeinModalProviderProps> = ({
    children,
    defaultOpen = false,
}) => {
    const [isOpen, setIsOpen] = useState(defaultOpen);

    const openModal = () => setIsOpen(true);
    const closeModal = () => setIsOpen(false);
    const toggleModal = () => setIsOpen(prev => !prev);

    const value = {
        isOpen,
        openModal,
        closeModal,
        toggleModal,
    };

    return (
        <MeinModalContext.Provider value={value}>
            {children}
        </MeinModalContext.Provider>
    );
};

export const useMeinModal = (): IMeinModalContext => {
    const context = useContext(MeinModalContext);

    if (!context) {
        throw new Error('useMeinModal must be used within a MeinModalProvider');
    }

    return context;
};

export const withMeinModal = <P extends object>(WrappedComponent: FC<P>) => {
    function ComponentWithModalContext(props: P) {
        const context = useContext(MeinModalContext);

        if (context) {
            return <WrappedComponent {...props} />;
        }

        return (
            <MeinModalProvider>
                <WrappedComponent {...props} />
            </MeinModalProvider>
        );
    }

    return ComponentWithModalContext;
};
